// RestaurantLayout.jsx
import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, UtensilsCrossed, ShoppingBag, Settings, LogOut, Menu, X, Calendar } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/dashboard/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/dashboard/bookings", icon: Calendar, label: "Bookings" },
  { to: "/dashboard/menu", icon: UtensilsCrossed, label: "Menu" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export function RestaurantLayout() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🍽️</span>
          <div>
            <p className="font-display font-bold text-white">Khana Spot</p>
            <p className="text-xs text-blue-300">Restaurant Panel</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isActive ? "bg-white/20 text-white" : "text-blue-200 hover:bg-white/10 hover:text-white"
              }`
            }>
            <Icon className="w-5 h-5" />{label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center text-sm font-bold text-white">
            {(profile?.name || "R")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{profile?.name}</p>
            <p className="text-blue-300 text-xs">Restaurant Owner</p>
          </div>
        </div>
        <button onClick={handleSignOut} className="flex items-center gap-2 w-full px-4 py-2.5 text-blue-200 hover:text-white hover:bg-white/10 rounded-xl text-sm transition-all">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <aside className="hidden lg:flex flex-col w-64 bg-gradient-to-b from-blue-900 to-blue-800 flex-shrink-0">
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 bg-gradient-to-b from-blue-900 to-blue-800 h-full z-10">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white"><X className="w-5 h-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-700"><Menu className="w-6 h-6" /></button>
          <h1 className="text-lg font-semibold text-gray-900 font-display">Restaurant Dashboard</h1>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6"><Outlet /></main>
      </div>
    </div>
  );
}

export default RestaurantLayout;
